import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useUserStore from '@/user/userStore'
import useAccountStore from './accountStore'
import useKycStore from '@/kyc/kycStore'
import { API_ROUTES } from '@/common/constants'
import { fetchAccounts } from './accountApi'
import type { Account } from '@/common/types'

const isTargetAccountPresent = (accounts: Account[], targetId: string): boolean =>
    accounts.some((account) => account.accountId === targetId)

export const useAccountSwitcher = () => {
    const queryClient = useQueryClient()
    const setActiveAccountId = useAccountStore((s) => s.setActiveAccountId)
    const startAccountSwitch = useAccountStore((s) => s.startAccountSwitch)
    const finishAccountSwitch = useAccountStore((s) => s.finishAccountSwitch)
    const setKyc = useKycStore((s) => s.setKyc)
    const setKycComplete = useKycStore((s) => s.setKycComplete)

    return useCallback(
        async (newId: string | null, options?: { refreshMetadata?: boolean }): Promise<boolean> => {
            const { refreshMetadata = true } = options ?? {}
            const { activeAccountId: prev } = useAccountStore.getState()
            const { user } = useUserStore.getState()

            if (prev === newId) {
                finishAccountSwitch()
                return true
            }

            startAccountSwitch(newId)

            const isAccountScopedQuery = (queryKey: readonly unknown[]): boolean => {
                if (!Array.isArray(queryKey) || queryKey.length < 2) return false
                const secondElement = queryKey[1]
                return typeof secondElement === 'string' && secondElement === prev
            }

            const accountScopedPredicate = (query: { queryKey: readonly unknown[] }) => {
                return isAccountScopedQuery(query.queryKey)
            }

            try {
                await queryClient.cancelQueries({ predicate: accountScopedPredicate })
                await queryClient.removeQueries({ predicate: accountScopedPredicate })
            } catch {
                // ignore
            } finally {
                setKyc(null)
                setKycComplete(false)
                setActiveAccountId(newId)
            }

            // For null resets there's nothing to confirm - finish immediately.
            if (newId === null) {
                finishAccountSwitch()
                return true
            }

            // Skipped for system self-corrections (e.g. resolving a stale persisted
            // activeAccountId) that already used freshly-fetched data to pick newId —
            // it's already known-good, so there's nothing to confirm.
            if (!refreshMetadata || !user?.userId) {
                return true
            }

            // Confirm the target account actually exists server-side before trusting the
            // switch. A plain invalidateQueries() only refetches when some component
            // happens to be observing this query at that moment - not guaranteed, since a
            // caller can switch accounts right before navigating to a route mounted under a
            // different AccountProvider instance. Fetching directly here guarantees a real
            // answer and lets us roll back immediately on a bad target, instead of leaving
            // isAccountSwitching stuck true forever waiting for a settlement that can never
            // arrive.
            try {
                const accounts = await queryClient.fetchQuery({
                    queryKey: [API_ROUTES.accounts, user.userId],
                    queryFn: fetchAccounts,
                    staleTime: 0,
                })

                if (!isTargetAccountPresent(accounts, newId)) {
                    setActiveAccountId(prev)
                    finishAccountSwitch()
                    return false
                }

                return true
            } catch {
                setActiveAccountId(prev)
                finishAccountSwitch()
                return false
            }
        },
        [queryClient, setActiveAccountId, setKyc, setKycComplete, startAccountSwitch, finishAccountSwitch],
    )
}

export default useAccountSwitcher
